import { useEffect } from 'react';
import { cn } from '@/shared/lib/utils';
import { useLanguage } from '@/shared/hooks/use-language';
import type { SupportedLanguage } from '@/shared/lib/i18n';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'pt-BR', label: 'PT' },
] as const;

/**
 * Compact EN/PT segmented language switcher.
 * Backed by the persisted `useLanguage` store (key `loki-language`), so the
 * choice survives reloads and applies across the whole site — the same key
 * i18n reads on boot.
 */
export function LanguageSwitcher() {
  const language = useLanguage((s) => s.language);
  const setLanguage = useLanguage((s) => s.setLanguage);
  const current: SupportedLanguage = language === 'pt-BR' ? 'pt-BR' : 'en';

  // Mirror the active language onto <html lang> for assistive tech.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = current;
    }
  }, [current]);

  const handle = (code: SupportedLanguage) => setLanguage(code);

  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-border/60 bg-muted/40 p-0.5">
      {LANGUAGES.map((lang) => (
        <button
          key={lang.code}
          type="button"
          onClick={() => handle(lang.code)}
          className={cn(
            'rounded px-2.5 py-1 font-mono text-[10.5px] font-semibold tracking-wider transition-colors',
            current === lang.code
              ? 'bg-amber-500/15 text-amber-500'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {lang.label}
        </button>
      ))}
    </div>
  );
}
